/**
 * Owner (2026-10-06, production screenshot of the old card): "change client
 * box — let's keep it like the original design for now." Fix O reverts this
 * View from the M1a token card (`PARITY.md` I2) back to the old
 * `ConnectionModal` (`PlayChess/components/ConnectionModal/index.js`),
 * reused BY IMPORT and never edited — the same precedent as `ReviewGames`
 * (`pages/home/variants/classic/HomePage.View.tsx`) / `ChallengeRejected`
 * (D5). The old component returns null when not visible and defaults to
 * its "another tab" face, exactly like the old screen's
 * `visible={changeClient || !authComplete}` + `authFail` prop — so this
 * file carries no face logic of its own; the only V2-side state left is the
 * MODEL's (`model.ts`: faces, grace window, `onContinue`).
 *
 * Loaded through `./lazyConnectionModal` (a `React.lazy` boundary) with its
 * own `Suspense fallback={null}` here — this root-level overlay has no
 * ancestor Suspense to borrow. A static import of the old component would
 * trip the eager-import guard (`__tests__/eagerGraph.test.ts`) from inside
 * `overlay/`, hence the indirection.
 *
 * REVIEW (2026-10-06): this unit mounts at the SCREEN ROOT for every
 * PlayChessV2 session, so rendering `<Suspense><ConnectionModal/></Suspense>`
 * UNCONDITIONALLY would invoke the lazy ctor — the dynamic import of the old
 * component, which drags `@/Atoms/CCText` → `@/Utils/CommonUtils` →
 * `StackConstants` — on every mount, for every user, not only a stranded
 * tab. Gated below on `visible` instead, keeping the View a pure memo with
 * one extra early return. Accepted trade-off: a genuinely stranded tab
 * only starts that chunk load once `visible` flips true, so it sees the
 * card one chunk-load later rather than it being prefetched; the lobby and
 * every ordinary game never pay for it at all.
 */
import React, {Suspense} from 'react';

import {ClientChangedModel} from './model';
import ConnectionModal from './lazyConnectionModal';

/** `onClose` has no default in the old component's destructure, so its
 * inferred JS type marks it required — but the one button that would ever
 * call it ("Navigate to Dashboard") is commented out there, so it is dead
 * either way. A no-op satisfies the type without inventing behaviour the
 * old screen itself does not run (same unused prop there has `onClose` as
 * the old screen's own `handleClose`, which this tree has no equivalent
 * of). */
const noop = () => {};

export default React.memo(function ClientChangedView({
  visible,
  authFailed,
  onContinue,
}: ClientChangedModel) {
  if (!visible) {
    return null;
  }
  return (
    <Suspense fallback={null}>
      <ConnectionModal
        visible={visible}
        authFail={authFailed}
        onContinue={onContinue}
        onClose={noop}
      />
    </Suspense>
  );
});
